import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getFirestoreForRegion, getBackendUrl, getActiveRegions } from './regionManager';
import { buildEndpoints } from '../config';

const RegionContext = createContext(null);

export const GLOBAL_REGION = 'GLOBAL';

export const useRegionContext = () => {
  const ctx = useContext(RegionContext);
  if (!ctx) {
    // Fallback for components not wrapped in RegionProvider — use AU defaults
    return {
      activeRegion: 'AU',
      setActiveRegion: () => {},
      db: null,
      endpoints: null,
      loading: true,
      isGlobal: false,
      allRegionDbs: null,
      allEndpoints: null,
    };
  }
  return ctx;
};

export const RegionProvider = ({ initialRegion = 'AU', children }) => {
  const [activeRegion, setActiveRegion] = useState(initialRegion);
  const [db, setDb] = useState(null);
  const [endpoints, setEndpoints] = useState(null);
  const [allRegionDbs, setAllRegionDbs] = useState(null);
  const [allEndpoints, setAllEndpoints] = useState(null);
  const [loading, setLoading] = useState(true);

  const initRegion = useCallback(async (code) => {
    setLoading(true);
    try {
      if (code === GLOBAL_REGION) {
        // Global mode: initialise Firestore for every active region
        const regionCodes = await getActiveRegions();
        const dbMap = {};
        const epMap = {};
        await Promise.all(regionCodes.map(async (rc) => {
          dbMap[rc] = await getFirestoreForRegion(rc);
          epMap[rc] = buildEndpoints(getBackendUrl(rc));
        }));
        setAllRegionDbs(dbMap);
        setAllEndpoints(epMap);
        // Use AU as the default single db for components that don't support global yet
        setDb(dbMap['AU'] || Object.values(dbMap)[0]);
        setEndpoints(epMap['AU'] || Object.values(epMap)[0]);
      } else {
        const firestore = await getFirestoreForRegion(code);
        const backendUrl = getBackendUrl(code);
        setDb(firestore);
        setEndpoints(buildEndpoints(backendUrl));
        setAllRegionDbs(null);
        setAllEndpoints(null);
      }
    } catch (err) {
      console.error(`Failed to init region ${code}:`, err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initRegion(activeRegion);
  }, [activeRegion, initRegion]);

  const handleSetRegion = useCallback((code) => {
    setActiveRegion(code);
    localStorage.setItem('piatto_admin_region', code);
  }, []);

  const isGlobal = activeRegion === GLOBAL_REGION;

  return (
    <RegionContext.Provider value={{
      activeRegion,
      setActiveRegion: handleSetRegion,
      db,
      endpoints,
      loading,
      isGlobal,
      allRegionDbs,
      allEndpoints,
    }}>
      {children}
    </RegionContext.Provider>
  );
};

export default RegionContext;
